/**
 * ProjectFormModal — create a project, or edit an existing one.
 *
 * One component for both because the fields are identical and the mental model
 * is the same: a project is a name, a short description, and the standing brief
 * every agent in it receives. Splitting them would guarantee the two dialogs
 * drift.
 *
 * The context brief gets the most room and the most explanation, because it is
 * the field that actually changes what the agents do and the one users are
 * least likely to guess the purpose of.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '@/hooks/useLanguage';
import type { ProjectSummary } from '../types';
import {
  PROJECT_COLORS, PROJECT_ICONS, ProjectGlyph, resolveProjectColor,
} from './ProjectIdentity';
import '../styles/base.css';

export interface ProjectFormValues {
  project_name: string;
  project_description: string;
  project_context_prompt: string;
  project_color: string;
  project_icon: string;
}

interface ProjectFormModalProps {
  isOpen: boolean;
  /** Present → edit mode; absent → create mode. */
  project?: ProjectSummary | null;
  onClose: () => void;
  onSubmit: (values: ProjectFormValues) => Promise<void>;
}

export function ProjectFormModal({ isOpen, project, onClose, onSubmit }: ProjectFormModalProps) {
  const { t } = useLanguage();
  const isEdit = !!project;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contextPrompt, setContextPrompt] = useState('');
  const [color, setColor] = useState<string>('indigo');
  const [icon, setIcon] = useState<string>('board');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Re-seed on every open: reusing the previous open's values would silently
  // carry one project's brief into another.
  useEffect(() => {
    if (!isOpen) return;
    setName(project?.project_name || '');
    setDescription(project?.project_description || '');
    setContextPrompt(project?.project_context_prompt || '');
    setColor(project?.project_color || 'indigo');
    setIcon(project?.project_icon || 'board');
    setError(null);
    // Focus after the portal has painted — but ONLY if the user has not already
    // put the caret somewhere themselves. A bare `focus()` on a timer will yank
    // the caret out of whichever field they started typing in first, and
    // whatever they type next lands in the name box appended to the name. That
    // is not theoretical: it produced a project literally called
    // "Summer Camp 2026Planning the season together." during the design walk.
    const id = window.setTimeout(() => {
      const active = document.activeElement;
      if (!active || active === document.body) nameRef.current?.focus();
    }, 60);
    return () => window.clearTimeout(id);
  }, [isOpen, project]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, saving]);

  if (!isOpen) return null;

  const trimmedName = name.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmedName || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        project_name: trimmedName,
        project_description: description.trim(),
        project_context_prompt: contextPrompt.trim(),
        project_color: color,
        project_icon: icon,
      });
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.error
          || (t('projects.form.saveFailed') || 'Could not save the project. Please try again.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="pj-overlay" onClick={() => !saving && onClose()} role="presentation">
      <form
        className="pj-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit
          ? (t('projects.form.editTitle') || 'Edit project')
          : (t('projects.form.createTitle') || 'New project')}
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <header className="pj-modal-header">
          <div className="pj-modal-heading">
            <h2 className="pj-modal-title">
              {isEdit
                ? (t('projects.form.editTitle') || 'Edit project')
                : (t('projects.form.createTitle') || 'New project')}
            </h2>
            <p className="pj-modal-subtitle">
              {t('projects.form.subtitle')
                || 'A shared space with its own brief and files, applied to every conversation inside it.'}
            </p>
          </div>
          <button
            type="button"
            className="pj-icon-btn"
            onClick={onClose}
            disabled={saving}
            aria-label={t('common.close') || 'Close'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="pj-modal-body">
          {error && <div className="pj-form-error">{error}</div>}

          <div className="pj-field">
            <label className="pj-label" htmlFor="pj-project-name">
              {t('projects.form.nameLabel') || 'Project name'}
            </label>
            <input dir="auto"
              id="pj-project-name"
              ref={nameRef}
              className="pj-input"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={255}
              placeholder={t('projects.form.namePlaceholder') || 'Summer Camp 2026'}
              required
            />
          </div>

          {/* Identity. A list of projects is a wall of near-identical cards;
              a colour and a glyph are what make one recognisable before its
              name is read. Both carry through to the project's own page. */}
          <div className="pj-field">
            <span className="pj-label">
              {t('projects.form.identityLabel') || 'Colour and icon'}
            </span>
            <div className="identity-row">
              <span
                className="identity-preview"
                style={{ ['--project-accent' as string]: resolveProjectColor(color) }}
                aria-hidden="true"
              >
                <ProjectGlyph icon={icon} strokeWidth={1.7} />
              </span>

              <div className="identity-choices">
                <div className="identity-swatches" role="radiogroup"
                     aria-label={t('projects.form.colourLabel') || 'Colour'}>
                  {PROJECT_COLORS.map(name => (
                    <button
                      key={name}
                      type="button"
                      role="radio"
                      aria-checked={color === name}
                      aria-label={name}
                      className={`identity-swatch ${color === name ? 'is-active' : ''}`}
                      style={{ ['--project-accent' as string]: resolveProjectColor(name) }}
                      onClick={() => setColor(name)}
                    />
                  ))}
                  {/* ...and any colour at all, for a team whose brand isn't
                      in the palette. */}
                  <label
                    className={`identity-swatch identity-swatch-custom ${color.startsWith('#') ? 'is-active' : ''}`}
                    style={{ ['--project-accent' as string]: color.startsWith('#') ? color : 'transparent' }}
                    title={t('projects.form.customColour') || 'Custom colour'}
                  >
                    <input
                      type="color"
                      value={color.startsWith('#') ? color : '#6366f1'}
                      onChange={e => setColor(e.target.value)}
                    />
                  </label>
                </div>

                <div className="identity-icons" role="radiogroup"
                     aria-label={t('projects.form.iconLabel') || 'Icon'}>
                  {PROJECT_ICONS.map(name => (
                    <button
                      key={name}
                      type="button"
                      role="radio"
                      aria-checked={icon === name}
                      aria-label={name}
                      className={`identity-icon ${icon === name ? 'is-active' : ''}`}
                      onClick={() => setIcon(name)}
                    >
                      <ProjectGlyph icon={name} strokeWidth={1.7} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="pj-field">
            <label className="pj-label" htmlFor="pj-project-description">
              {t('projects.form.descriptionLabel') || 'Description'}
              <span className="pj-label-optional"> · {t('common.optional') || 'optional'}</span>
            </label>
            <textarea dir="auto"
              id="pj-project-description"
              className="pj-textarea"
              style={{ minHeight: 64 }}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('projects.form.descriptionPlaceholder')
                || 'What this project is for, in a sentence.'}
            />
          </div>

          <div className="pj-field">
            <label className="pj-label" htmlFor="pj-project-context">
              {t('projects.form.contextLabel') || 'Project brief for the agents'}
              <span className="pj-label-optional"> · {t('common.optional') || 'optional'}</span>
            </label>
            <textarea dir="auto"
              id="pj-project-context"
              className="pj-textarea"
              value={contextPrompt}
              onChange={e => setContextPrompt(e.target.value)}
              placeholder={t('projects.form.contextPlaceholder')
                || 'Always check the budget sheet before quoting a number. Answers should be short and practical.'}
            />
            <p className="pj-hint">
              {t('projects.form.contextHint')
                || 'Every conversation started in this project receives this brief, along with the project files.'}
            </p>
          </div>
        </div>

        <footer className="pj-modal-footer">
          <button type="button" className="pj-btn pj-btn-secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel') || 'Cancel'}
          </button>
          <button type="submit" className="pj-btn pj-btn-primary" disabled={!trimmedName || saving}>
            {saving
              ? (t('common.saving') || 'Saving…')
              : isEdit
                ? (t('common.save') || 'Save')
                : (t('projects.form.createAction') || 'Create project')}
          </button>
        </footer>
      </form>
    </div>,
    document.body,
  );
}

export default ProjectFormModal;
